/* eslint-disable react/function-component-definition */
import React, { useEffect, useState } from 'react';
import { Router } from 'react-router';
import { Route, Switch, Redirect, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import history from '@AppRoutes/history';
import ConnectedRoute from '@AppRoutes/ConnectedRoute';
import DNSMaintenance from '@Pages/DNSMaintenance';
import Login from '@Services/Login';
import Logout from '@Pages/Logout';
import LoginLimit from '@Pages/LoginLimit';
import LoginContainer from '@Pages/Login';
import OrganizationContainer from '@Pages/Login/Organization';
import InstitutionContainer from '@Pages/Login/Institution';
import ShibLogin from '@Pages/Login/ShibLogin';
import RemoteAccessLogin from '@Pages/Login/RemoteAccessLogin';
import BulkIdRegister from '@Pages/Login/BulkIdRegister';
import RegisterFinalize from '@Pages/Login/RegisterFinalize';
import RegisterConfirmation from '@Pages/Login/RegisterConfirmation';
import Register from '@Pages/Login/Register';
import Maintenance from '@Pages/Maintenance';
import AboutContent from '@Pages/AboutContent';
import ErrorPage from '@Pages/Error';
import AppStorage from '@Services/AppStorage';
import NavigationHistory from '@Services/NavigationHistory';
import Heartbeat from '@Services/Heartbeat';
import Auth from '@Services/Auth';
import { SkipLinks } from '@els/els-react--skip-links';
import PageTitleUpdater from '@Components/PageTitleUpdater/PageTitleUpdater';
import Main from '../Main/Main';
import { paths, skipLinkMappings } from './constants';
import PageLoadingTimeout from './PageLoadingTimeout';

import './AppRoutes.scss';

function checkUser(location) {
  let user;

  const userGetInfo = () => {
    // there is access token, but still need to check RX2_APPLICATION permission
    if (!Auth.isReaxysAllowed()) {
      // Anonymous with enforced login case
      // eslint-disable-next-line prefer-promise-reject-errors
      return Promise.reject('RX2_APPLICATION is missing in permissions');
    }

    return Auth.getUserInfo()
      .then((data) => Auth.changeUserProfile(true, data))
      .catch(Logger.reject);
  };

  const userRecoverSession = () => {
    if (location.pathname.indexOf('/hopinto') >= 0) {
      const hopintoPath = `${location.pathname}${location.search}`;
      AppStorage.instance.set('hopintoPath', hopintoPath);
    }
    return Auth.recoverSession(true).then(userGetInfo).then(Login.afterAuthRedirect).catch(Logger.reject);
  };

  if (!Auth.getAccessToken()) {
    // in case there is no session
    Auth.cleanUp();

    // get REMEMBERED OR ANONYMOUS USER, will be automaticaly redirected to login on error
    user = userRecoverSession();
  } else if (Heartbeat.isSessionTimeoutOneTimeCheck(true)) {
    // session expired while browser was closed
    Logger.info('Session expired while browser was closed');

    // logout, then recover session
    user = Auth.logout(!Auth.getRememberMeToken())
      .then(() => {
        // Clear navigation history, so after heartbeat logout it will redirect to landing
        NavigationHistory.hardClear();
      })
      .then(userRecoverSession, userRecoverSession)
      .catch(Logger.reject);
  } else {
    // there is access token
    user = userGetInfo();
  }
  return user;
}

const MainRoute = ({ component: Component, ...rest }) => {
  const [authenticated, setAuthenticated] = useState(undefined);
  const location = useLocation();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        // authentication check
        await checkUser(location);
        setAuthenticated(true);
      } catch (error) {
        Logger.debug(error);
        setAuthenticated(false);
      }
    };

    checkAuthentication();
  }, []);

  if (authenticated === undefined) {
    return (
      <div
        className="rx-loader--container rx-loader--container__page-pending rx-loader--scale-1_5x"
        id="page-pending"
        aria-hidden="false"
        aria-label="loading content"
        role="status">
        <div id="hexagon-visuals" />
        <div className="spinner-animation spinner-animation__page-pending spinner-animation--scale-1_5x" />
        <PageLoadingTimeout />
      </div>
    );
  }
  return <Route {...rest} render={(props) => (authenticated ? <Component {...props} /> : <Redirect to={paths.LOGIN} />)} />;
};

const PrivateRoute = ({ component: Component, ...rest }) => {
  const user = Auth.getUser();
  const permission = user && user.permissions && user.permissions.length > 0;
  return <ConnectedRoute {...rest} render={(props) => (permission ? <Component {...props} /> : <Redirect to={paths.LOGIN} />)} />;
};

function AppRouter() {
  return (
    <Router history={history}>
      <PageTitleUpdater />
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex */}
      <section id="skip-link-wrap" tabIndex="0">
        <SkipLinks id="skip-links" sections={skipLinkMappings} />
      </section>
      <Switch>
        <ConnectedRoute exact path={paths.LOGOUT} component={Logout} />
        <ConnectedRoute exact path={paths.LOGIN_LIMIT} component={LoginLimit} />
        <ConnectedRoute exact path={paths.LOGIN} component={LoginContainer} />
        <ConnectedRoute exact path={paths.LOGIN_ORGANIZATION} component={OrganizationContainer} />
        <ConnectedRoute exact path={paths.LOGIN_INSTITUTION} component={InstitutionContainer} />
        <ConnectedRoute exact path={paths.LOGIN_SHIBBOLETH} component={ShibLogin} />
        <ConnectedRoute exact path={paths.LOGIN_SELF_MANAGED} component={RemoteAccessLogin} />
        <ConnectedRoute exact path={paths.DNS_MAINTENANCE} component={DNSMaintenance} />
        <ConnectedRoute exact path={paths.REGISTER_BULK} component={BulkIdRegister} />
        <ConnectedRoute exact path={paths.FINALIZE_REGISTER} component={RegisterFinalize} />
        <ConnectedRoute exact path={paths.REGISTER_CONFIRMATION} component={RegisterConfirmation} />
        <ConnectedRoute exact path={paths.REGISTER} component={Register} />
        <ConnectedRoute exact path={paths.MAINTENANCE} component={Maintenance} />
        <ConnectedRoute exact path={paths.ABOUT_CONTENT} component={AboutContent} />
        <ConnectedRoute exact path={paths.ERROR} component={ErrorPage} />
        <PrivateRoute path={paths.PRIVATE_REGISTER} component={Register} />
        <PrivateRoute path={paths.PRIVATE_FINALIZE_REGISTER} component={RegisterFinalize} />
        <MainRoute component={Main} />
      </Switch>
    </Router>
  );
}

MainRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
};

PrivateRoute.propTypes = {
  component: PropTypes.elementType.isRequired,
};

export default AppRouter;
